function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

/* CONTROLES GLOBALES DE CARRUSELES KARTIS */
/* Pegar una sola vez al final de assets/global.js o assets/theme.js. */

(() => {
  const sliderSelector = '#MainContent .slider-component';

  const getParts = (slider) => {
    const list = slider.querySelector(':scope > ul.slider, :scope > .slider');
    const controls = slider.querySelector(':scope > .slider-buttons, .slider-buttons');
    if (!list || !controls) return null;

    const items = [...list.children].filter((item) =>
      item.classList.contains('slider__slide') ||
      item.classList.contains('grid__item')
    );

    return { list, controls, items };
  };

  const getStep = (list, items) => {
    if (!items.length) return 0;
    const gap = parseFloat(getComputedStyle(list).columnGap || getComputedStyle(list).gap || '0');
    return items[0].getBoundingClientRect().width + gap;
  };

  const getPages = (list, items) => {
    const step = getStep(list, items);
    if (!step || list.scrollWidth <= list.clientWidth + 2) return 0;
    return Math.max(2, Math.ceil((list.scrollWidth - list.clientWidth) / step) + 1);
  };

  const getCurrent = (list, items) => {
    const step = getStep(list, items);
    return step ? Math.round(list.scrollLeft / step) : 0;
  };

  const render = (slider, list, items) => {
    const pages = getPages(list, items);
    let indicators = slider.querySelector(':scope > .kartis-slider-indicators');

    if (!pages) {
      indicators?.remove();
      return;
    }

    if (!indicators) {
      indicators = document.createElement('div');
      indicators.className = 'kartis-slider-indicators';
      indicators.setAttribute('role', 'tablist');
      indicators.setAttribute('aria-label', 'Páginas del carrusel');
      slider.appendChild(indicators);
    }

    const oldCount = indicators.querySelectorAll('.kartis-slider-indicators__dot').length;
    if (oldCount !== pages) {
      indicators.replaceChildren();

      for (let index = 0; index < pages; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'kartis-slider-indicators__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Ir a la página ${index + 1}`);
        dot.addEventListener('click', () => {
          list.scrollTo({
            left: getStep(list, items) * index,
            behavior: 'smooth'
          });
        });
        indicators.appendChild(dot);
      }
    }

    const current = Math.min(pages - 1, getCurrent(list, items));
    [...indicators.children].forEach((dot, index) => {
      dot.setAttribute('aria-current', index === current ? 'true' : 'false');
    });
  };

  const enhance = (slider) => {
    if (slider.dataset.kartisGlobalIndicators === 'true') return;
    const parts = getParts(slider);
    if (!parts || parts.items.length < 2) return;

    slider.dataset.kartisGlobalIndicators = 'true';
    const { list, controls, items } = parts;
    controls.setAttribute('aria-hidden', 'true');
    render(slider, list, items);

    let scrollTimer;
    list.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => render(slider, list, items), 60);
    }, { passive: true });

    window.addEventListener('resize', () => render(slider, list, items));
  };

  const init = () => {
    document.querySelectorAll(sliderSelector).forEach(enhance);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  new MutationObserver(init).observe(document.body, { childList: true, subtree: true });
})();

/* SOLO CÁPSULAS PARA CARRUSELES — VERSIÓN CORREGIDA */
/* Reemplazar el bloque anterior de cápsulas en global.js. */

(() => {
  const SELECTOR = '#MainContent slider-component, #MainContent .slider-component';

  function getSliderParts(slider) {
    const list = slider.querySelector('ul.slider, .slider');
    const controls = slider.querySelector('.slider-buttons');
    const counter = controls?.querySelector('.slider-counter.caption');

    if (!list || !controls || !counter) return null;

    const items = [...list.children].filter((item) =>
      item.classList.contains('slider__slide') ||
      item.classList.contains('grid__item')
    );

    if (items.length < 2) return null;
    return { list, controls, items };
  }

  function getStep(list, items) {
    const first = items[0];
    if (!first) return 0;

    const style = getComputedStyle(list);
    const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  function getPageCount(list, items) {
    const step = getStep(list, items);
    if (!step || list.clientWidth === 0) return 0;
    if (list.scrollWidth <= list.clientWidth + 2) return 1;
    return Math.max(2, Math.ceil((list.scrollWidth - list.clientWidth) / step) + 1);
  }

  function updateIndicators(slider, list, items, indicators) {
    const pageCount = getPageCount(list, items);
    if (!pageCount) return;

    while (indicators.children.length < pageCount) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'kartis-slider-indicators__dot';
      dot.setAttribute('role', 'tab');
      indicators.appendChild(dot);
    }

    while (indicators.children.length > pageCount) {
      indicators.lastElementChild.remove();
    }

    const step = getStep(list, items);
    const current = Math.min(pageCount - 1, Math.round(list.scrollLeft / step));

    [...indicators.children].forEach((dot, index) => {
      dot.setAttribute('aria-label', `Ir a la página ${index + 1}`);
      dot.setAttribute('aria-current', index === current ? 'true' : 'false');
      dot.onclick = () => list.scrollTo({
        left: step * index,
        behavior: 'smooth'
      });
    });
  }

  function enhance(slider) {
    if (slider.dataset.kartisCapsulasCorregido === 'true') return;

    const parts = getSliderParts(slider);
    if (!parts) return;

    const { list, controls, items } = parts;
    slider.dataset.kartisCapsulasCorregido = 'true';
    controls.setAttribute('aria-hidden', 'true');

    let indicators = slider.querySelector(':scope > .kartis-slider-indicators');
    if (!indicators) {
      indicators = document.createElement('div');
      indicators.className = 'kartis-slider-indicators';
      indicators.setAttribute('role', 'tablist');
      indicators.setAttribute('aria-label', 'Páginas del carrusel');
      slider.appendChild(indicators);
    }

    const refresh = () => updateIndicators(slider, list, items, indicators);
    refresh();

    let scrollTimer;
    list.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(refresh, 80);
    }, { passive: true });

    window.addEventListener('resize', refresh);
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  }

  init();
  window.addEventListener('load', init, { once: true });
  window.setTimeout(init, 300);
  window.setTimeout(init, 1000);

  new MutationObserver(init).observe(document.body, {
    childList: true,
    subtree: true
  });
})();
/* =========================================================
   KARTIS - SELECTOR FUNCIONAL GRID / LISTA DE CATEGORÍAS
   ========================================================= */
(() => {
  const iniciarSelectorCategorias = () => {
    const pagina = document.querySelector('.kartis-categories-page');
    if (!pagina || pagina.dataset.kartisViewReady === 'true') return;

    const botones = pagina.querySelectorAll('[data-kartis-view]');
    const buscador = pagina.querySelector('[data-kartis-category-search]');
    const items = [...pagina.querySelectorAll('.collection-list__item')];

    if (!botones.length) return;

    pagina.dataset.kartisViewReady = 'true';

    const aplicarVista = (vista) => {
      const vistaValida = vista === 'list' ? 'list' : 'grid';

      pagina.classList.toggle('is-list-view', vistaValida === 'list');
      pagina.dataset.kartisView = vistaValida;

      botones.forEach((boton) => {
        const activo = boton.dataset.kartisView === vistaValida;
        boton.classList.toggle('is-active', activo);
        boton.setAttribute('aria-pressed', activo ? 'true' : 'false');
      });

      try {
        localStorage.setItem('kartis-categories-view', vistaValida);
      } catch (error) {
        /* Continúa funcionando aunque el navegador bloquee localStorage */
      }
    };

    botones.forEach((boton) => {
      boton.addEventListener('click', () => {
        aplicarVista(boton.dataset.kartisView);
      });
    });

    if (buscador) {
      buscador.addEventListener('input', () => {
        const texto = buscador.value.trim().toLocaleLowerCase();

        items.forEach((item) => {
          const titulo = item.querySelector('.card__heading')?.textContent
            .trim()
            .toLocaleLowerCase() || '';

          item.hidden = texto !== '' && !titulo.includes(texto);
        });
      });
    }

    let vistaGuardada = 'grid';

    try {
      vistaGuardada = localStorage.getItem('kartis-categories-view') || 'grid';
    } catch (error) {
      vistaGuardada = 'grid';
    }

    aplicarVista(vistaGuardada);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciarSelectorCategorias, { once: true });
  } else {
    iniciarSelectorCategorias();
  }
})();
/* =========================================================
   KARTIS - FAVORITOS
   Un solo controlador para mouse, toque y teclado
   ========================================================= */
(() => {
  const STORAGE_KEY = 'kartis-favoritos';

  function leerFavoritos() {
    try {
      const guardados = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(guardados) ? guardados.map(String) : [];
    } catch (error) {
      return [];
    }
  }

  function guardarFavoritos(lista) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
    } catch (error) {
      // La tarjeta sigue funcionando aunque el navegador bloquee localStorage.
    }
  }

  function actualizarBoton(boton, activo) {
    boton.classList.toggle('is-favorite', activo);
    boton.setAttribute('aria-pressed', activo ? 'true' : 'false');
    boton.setAttribute(
      'aria-label',
      activo ? 'Quitar de favoritos' : 'Agregar a favoritos'
    );
  }

  function cargarEstadoFavoritos() {
    const favoritos = leerFavoritos();

    document.querySelectorAll('[data-kartis-favorite]').forEach((boton) => {
      const id = String(boton.dataset.productId || '');
      if (id) actualizarBoton(boton, favoritos.includes(id));
    });
  }

  function alternarFavorito(boton) {
    const id = String(boton.dataset.productId || '');
    if (!id) return;

    let favoritos = leerFavoritos();
    const activo = !favoritos.includes(id);

    favoritos = activo
      ? [...favoritos, id]
      : favoritos.filter((favorito) => favorito !== id);

    guardarFavoritos(favoritos);
    actualizarBoton(boton, activo);

    // Reinicia la animación para que se reproduzca en cada marcado/desmarcado.
    boton.classList.remove('kartis-heart-beat');
    void boton.offsetWidth;
    boton.classList.add('kartis-heart-beat');
  }

  // El evento click funciona correctamente con clic izquierdo, toque y teclado.
  document.addEventListener('click', (evento) => {
    const objetivo = evento.target;
    const boton = objetivo instanceof Element
      ? objetivo.closest('[data-kartis-favorite]')
      : null;

    if (!boton) return;

    evento.preventDefault();
    evento.stopPropagation();
    alternarFavorito(boton);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cargarEstadoFavoritos, { once: true });
  } else {
    cargarEstadoFavoritos();
  }

  // Shopify puede reemplazar tarjetas mediante AJAX; vuelve a pintar su estado.
  document.addEventListener('shopify:section:load', cargarEstadoFavoritos);
  document.addEventListener('shopify:section:reorder', cargarEstadoFavoritos);
})();
/* =========================================================
   KARTIS - SINCRONIZAR BUSCADOR CON EL ENCABEZADO
   ========================================================= */

(() => {
  const searchInput = 'input[placeholder="Buscar entre miles de productos"]';

  function syncKartisSearch() {
    const header =
      document.querySelector('.section-header') ||
      document.querySelector('sticky-header') ||
      document.querySelector('header');

    const searchSection = document
      .querySelector(searchInput)
      ?.closest('#MainContent > .shopify-section');

    if (!header || !searchSection) return;

    const headerHeight = header.getBoundingClientRect().height;

    document.documentElement.style.setProperty(
      '--kartis-header-height',
      `${headerHeight}px`
    );
  }

  let animationFrame;

  function requestSync() {
    cancelAnimationFrame(animationFrame);
    animationFrame = requestAnimationFrame(syncKartisSearch);
  }

  window.addEventListener('scroll', requestSync, { passive: true });
  window.addEventListener('resize', requestSync);

  const header = document.querySelector('.section-header');

  if (header && 'ResizeObserver' in window) {
    const observer = new ResizeObserver(requestSync);
    observer.observe(header);
  }

  requestSync();
})();
/* =========================================================
   KARTIS - OCULTAR FLECHA IZQUIERDA EN LA PRIMERA DIAPOSITIVA
   ========================================================= */

(() => {
  function actualizarFlechaAnterior(slideshow) {
    const slider = slideshow.querySelector('.slideshow');
    const flechaAnterior = slideshow.querySelector(
      'button.slider-button--prev'
    );

    if (!slider || !flechaAnterior) return;

    const estaEnLaPrimera = slider.scrollLeft <= 2;

    slideshow.classList.toggle(
      'kartis-primera-diapositiva',
      estaEnLaPrimera
    );
  }

  function iniciarControlDeFlecha() {
    document.querySelectorAll('slideshow-component').forEach((slideshow) => {
      const slider = slideshow.querySelector('.slideshow');

      if (!slider || slider.dataset.kartisFlechaControl === 'true') return;

      slider.dataset.kartisFlechaControl = 'true';

      slider.addEventListener('scroll', () => {
        actualizarFlechaAnterior(slideshow);
      });

      window.addEventListener('resize', () => {
        actualizarFlechaAnterior(slideshow);
      });

      setTimeout(() => {
        actualizarFlechaAnterior(slideshow);
      }, 300);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciarControlDeFlecha);
  } else {
    iniciarControlDeFlecha();
  }

  document.addEventListener(
    'shopify:section:load',
    iniciarControlDeFlecha
  );
})();
/* =========================================================
   KARTIS - ENLACE EN LA PRIMERA DIAPOSITIVA DE WHATSAPP
   No modifica el tamaño de la imagen
   ========================================================= */

(function () {
  const whatsappChannelUrl =
    'https://whatsapp.com/channel/0029VaeIe709hXF2fFqzGa2O';

  function activateWhatsAppSlide( ) {
    const section = document.querySelector(
      '#shopify-section-template--20939150164147__slideshow_63xLCE'
    );

    if (!section || section.dataset.whatsappLinkReady === 'true') {
      return;
    }

    const firstSlide = section.querySelector(
      '.slideshow__slide:first-child, [id^="Slide-"]:first-of-type'
    );

    if (!firstSlide) {
      return;
    }

    firstSlide.dataset.whatsappLinkReady = 'true';
    firstSlide.style.cursor = 'pointer';

    firstSlide.addEventListener('click', function (event) {
      /*
       * No interferir con botones ni controles del carrusel.
       */
      if (event.target.closest('button, a')) {
        return;
      }

      window.location.href = whatsappChannelUrl;
    });

    section.dataset.whatsappLinkReady = 'true';
  }

  document.addEventListener('DOMContentLoaded', activateWhatsAppSlide);
  window.addEventListener('load', activateWhatsAppSlide);
  document.addEventListener('shopify:section:load', activateWhatsAppSlide);
})();
/* =========================================================
   KARTIS - CONFETI VISIBLE TIPO FUEGOS ARTIFICIALES
   ========================================================= */

(function () {
  function lanzarConfeti(boton) {
    const colores = [
      '#8231e7',
      '#f5c542',
      '#ff6b8a',
      '#35c98b',
      '#1d9bf0',
      '#ff8c42'
    ];

    const formas = [
      {
        width: '4px',
        height: '10px',
        radius: '2px'
      },
      {
        width: '6px',
        height: '6px',
        radius: '50%'
      },
      {
        width: '5px',
        height: '9px',
        radius: '1px'
      },
      {
        width: '7px',
        height: '4px',
        radius: '2px'
      }
    ];

    const rect = boton.getBoundingClientRect();
    const centroX = rect.left + rect.width / 2;
    const centroY = rect.top + rect.height / 2;
    const cantidad = 16;

    for (let i = 0; i < cantidad; i++) {
      const particula = document.createElement('span');

      const angulo =
        (Math.PI * 2 * i) / cantidad +
        (Math.random() - 0.5) * 0.3;

      const distancia = 28 + Math.random() * 12;
      const distanciaFinal = distancia + 8 + Math.random() * 10;
      const forma = formas[Math.floor(Math.random() * formas.length)];

      particula.className = 'kartis-heart-confetti';

      particula.style.setProperty(
        '--kartis-confetti-left',
        `${centroX}px`
      );

      particula.style.setProperty(
        '--kartis-confetti-top',
        `${centroY}px`
      );

      particula.style.setProperty(
        '--kartis-confetti-width',
        forma.width
      );

      particula.style.setProperty(
        '--kartis-confetti-height',
        forma.height
      );

      particula.style.setProperty(
        '--kartis-confetti-radius',
        forma.radius
      );

      particula.style.setProperty(
        '--kartis-confetti-x',
        `${Math.cos(angulo) * distancia}px`
      );

      particula.style.setProperty(
        '--kartis-confetti-y',
        `${Math.sin(angulo) * distancia}px`
      );

      particula.style.setProperty(
        '--kartis-confetti-x-end',
        `${Math.cos(angulo) * distanciaFinal}px`
      );

      particula.style.setProperty(
        '--kartis-confetti-y-end',
        `${Math.sin(angulo) * distanciaFinal}px`
      );

      particula.style.setProperty(
        '--kartis-confetti-rotate',
        `${Math.floor(Math.random() * 360)}deg`
      );

      particula.style.setProperty(
        '--kartis-confetti-color',
        colores[Math.floor(Math.random() * colores.length)]
      );

      document.body.appendChild(particula);

      window.setTimeout(function () {
        particula.remove();
      }, 700);
    }
  }

  function activarAnimacionFavoritos() {
    if (
      document.documentElement.dataset.kartisFavoriteAnimation === 'true'
    ) {
      return;
    }

    document.documentElement.dataset.kartisFavoriteAnimation = 'true';

    document.addEventListener('click', function (evento) {
      const boton = evento.target.closest(
        '.kartis-favorite-button, .kartis-product-favorite-button'
      );

      if (!boton) {
        return;
      }

      boton.classList.remove('kartis-favorite-heart-beat');

      void boton.offsetWidth;

      boton.classList.add('kartis-favorite-heart-beat');

      lanzarConfeti(boton);

      window.setTimeout(function () {
        boton.classList.remove('kartis-favorite-heart-beat');
      }, 550);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      activarAnimacionFavoritos
    );
  } else {
    activarAnimacionFavoritos();
  }
})();
/* =========================================================
   KARTIS - OCULTAR FLECHA DERECHA EN LA ÚLTIMA DIAPOSITIVA
   ========================================================= */

(function () {
  function activarControlUltimaDiapositiva() {
    const slider = document.querySelector(
      '#MainContent slideshow-component[aria-label="Venta de artículos por departamento"]'
    );

    if (!slider || slider.dataset.kartisLastArrowReady === 'true') {
      return;
    }

    const lista = slider.querySelector('.slideshow');
    const flechaDerecha = slider.querySelector(
      '.slider-button--next'
    );

    if (!lista || !flechaDerecha) {
      return;
    }

    const diapositivas = [
      ...lista.querySelectorAll('.slideshow__slide')
    ];

    if (diapositivas.length === 0) {
      return;
    }

    slider.dataset.kartisLastArrowReady = 'true';

    function actualizarFlechaDerecha() {
      const posicionActual = lista.scrollLeft;
      let indiceActivo = 0;
      let menorDiferencia = Infinity;

      diapositivas.forEach((diapositiva, indice) => {
        const diferencia = Math.abs(
          diapositiva.offsetLeft - posicionActual
        );

        if (diferencia < menorDiferencia) {
          menorDiferencia = diferencia;
          indiceActivo = indice;
        }
      });

      const esUltima =
        indiceActivo === diapositivas.length - 1;

      flechaDerecha.hidden = esUltima;
      flechaDerecha.setAttribute(
        'aria-hidden',
        esUltima ? 'true' : 'false'
      );

      slider.classList.toggle(
        'kartis-ultima-diapositiva',
        esUltima
      );
    }

    lista.addEventListener(
      'scroll',
      actualizarFlechaDerecha,
      { passive: true }
    );

    flechaDerecha.addEventListener(
      'click',
      function () {
        window.setTimeout(
          actualizarFlechaDerecha,
          80
        );
      }
    );

    window.addEventListener(
      'resize',
      actualizarFlechaDerecha
    );

    actualizarFlechaDerecha();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      activarControlUltimaDiapositiva,
      { once: true }
    );
  } else {
    activarControlUltimaDiapositiva();
  }

  window.addEventListener(
    'load',
    activarControlUltimaDiapositiva,
    { once: true }
  );
})();
/* =========================================================
   KARTIS - PERFIL:
   PERMITIR SOLO EL DESLIZAMIENTO VERTICAL HACIA ABAJO
   ========================================================= */

(function () {
  function activarGestoPerfil() {
    const pagina = document.querySelector(
      '.kartis-account-welcome-page'
    );

    if (!pagina || pagina.dataset.kartisPullReady === 'true') {
      return;
    }

    pagina.dataset.kartisPullReady = 'true';

    let inicioX = 0;
    let inicioY = 0;
    let gestoVertical = false;

    pagina.addEventListener(
      'touchstart',
      function (evento) {
        if (!evento.touches || evento.touches.length !== 1) {
          return;
        }

        inicioX = evento.touches[0].clientX;
        inicioY = evento.touches[0].clientY;
        gestoVertical = false;
      },
      { passive: true }
    );

    pagina.addEventListener(
      'touchmove',
      function (evento) {
        if (!evento.touches || evento.touches.length !== 1) {
          return;
        }

        const actualX = evento.touches[0].clientX;
        const actualY = evento.touches[0].clientY;
        const diferenciaX = actualX - inicioX;
        const diferenciaY = actualY - inicioY;

        /*
         * Espera a que el gesto tenga una dirección clara.
         */
        if (
          !gestoVertical &&
          Math.abs(diferenciaY) > 8 &&
          Math.abs(diferenciaY) > Math.abs(diferenciaX)
        ) {
          gestoVertical = true;
        }

        /*
         * Si el dedo sube, se bloquea el desplazamiento.
         * Si baja, se deja continuar para el gesto de recarga.
         */
        if (gestoVertical && diferenciaY < 0) {
          evento.preventDefault();
        }
      },
      { passive: false }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      activarGestoPerfil,
      { once: true }
    );
  } else {
    activarGestoPerfil();
  }

  window.addEventListener(
    'load',
    activarGestoPerfil,
    { once: true }
  );
})();
